import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, MapPin, DollarSign, Heart, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const FavoritesPage = ({ user }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/jobs/favorites/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Error al cargar favoritos');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/jobs/${jobId}/favorite`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        setJobs(jobs.filter((j) => j.id !== jobId));
        toast.success('Eliminado de favoritos');
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Error al eliminar favorito');
    }
  };

  const getSpanishLevelBadge = (level) => {
    const variants = {
      native: { color: 'bg-red-100 text-red-700', label: 'Nativo' },
      advanced: { color: 'bg-red-100 text-red-600', label: 'Avanzado' },
      intermediate: { color: 'bg-orange-100 text-orange-700', label: 'Intermedio' },
      basic: { color: 'bg-amber-100 text-amber-700', label: 'Básico' },
      'not-required': { color: 'bg-gray-100 text-gray-700', label: 'No requerido' },
    };
    const variant = variants[level] || variants['not-required'];
    return <Badge className={`${variant.color} font-medium`}>{variant.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Mis Favoritos</h1>
          <p className="text-lg text-slate-600">Ofertas que has guardado para revisar más tarde</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando favoritos...</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="favorites-list">
            {jobs.length === 0 ? (
              <Card className="p-12 text-center">
                <Heart className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-xl font-semibold mb-2">Aún no tienes favoritos</h3>
                <p className="text-slate-600 mb-6">Guarda ofertas para acceder rápidamente más tarde</p>
                <Button onClick={() => navigate('/dashboard')} data-testid="browse-jobs-btn">
                  Explorar Ofertas
                </Button>
              </Card>
            ) : (
              jobs.map((job) => (
                <Card
                  key={job.id}
                  className="hover:shadow-xl transition-all"
                  data-testid={`favorite-job-${job.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 cursor-pointer" onClick={() => navigate(`/jobs/${job.id}`)}>
                        <h3 className="text-2xl font-bold mb-2 text-slate-900">{job.title}</h3>
                        <p className="text-lg font-medium text-red-600 mb-3">{job.company}</p>

                        <div className="flex flex-wrap gap-3 mb-4">
                          <div className="flex items-center text-slate-600">
                            <MapPin className="w-4 h-4 mr-1" />
                            <span>{job.location}</span>
                          </div>
                          {job.salary && (
                            <div className="flex items-center text-slate-600">
                              <DollarSign className="w-4 h-4 mr-1" />
                              <span>{job.salary}</span>
                            </div>
                          )}
                          <Badge variant="outline">{job.contract_type}</Badge>
                          <Badge variant="outline">{job.sector}</Badge>
                        </div>

                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600">Nivel de español:</span>
                          {getSpanishLevelBadge(job.spanish_level)}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/jobs/${job.id}`)}
                          data-testid={`view-job-${job.id}`}
                        >
                          Ver detalles
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFavorite(job.id)}
                          data-testid={`remove-favorite-${job.id}`}
                        >
                          <Heart className="w-4 h-4 mr-2 fill-current" />
                          Quitar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;